import { useEffect, RefObject } from 'react';

/**
 * Configures the A-Frame camera entity for AR mode.
 * 
 * @param cameraRef - RefObject pointing to the A-Frame camera entity (<a-entity>).
 * @param isSceneReady - Boolean indicating if the A-Frame scene has loaded.
 */
export function useCameraConfiguration(
  cameraRef: RefObject<any>, 
  isSceneReady: boolean
) {
  useEffect(() => {
    if (!isSceneReady || !cameraRef.current) {
      console.log('Camera config hook: Waiting for scene or camera ref...');
      return; 
    }
    
    const cameraEntity = cameraRef.current.el || cameraRef.current;
    if (!cameraEntity.setAttribute) {
      console.error('Camera config hook: cameraEntity does not have setAttribute method.', cameraEntity);
      return; // Not a valid A-Frame element
    }

    console.log(`Configuring camera for AR mode on entity:`, cameraEntity);

    // Detect device orientation support
    const supportsOrientation = 'DeviceOrientationEvent' in window;
    console.log(`Device supports orientation events: ${supportsOrientation}`);

    // Set base position for AR
    const initialPosition = '0 1.6 0'; 
    cameraEntity.setAttribute('position', initialPosition);

    // --- Camera configuration for manual AR placement --- 
    // Ensure gps-new-camera is disabled as we are manually controlling placement
    // cameraEntity.setAttribute('gps-new-camera', { gpsMinDistance: 5 }); 
    console.log('gps-new-camera component is disabled for manual AR placement logic.');

    // Configure look-controls based on orientation support
    cameraEntity.setAttribute('look-controls', { 
      enabled: true, 
      magicWindowTrackingEnabled: true, // ENABLED for device orientation
      touchEnabled: true, // Always allow touch/drag input
      // minPitch and maxPitch removed to allow full orientation-based pitch
      pointerLockEnabled: true 
    });
    console.log(`look-controls configured for device orientation and pointerLockEnabled: true`);

    // Clean up potentially leftover Dev specific components
    if (cameraEntity.hasAttribute('wasd-controls')) {
        cameraEntity.removeAttribute('wasd-controls');
        console.log('Cleaned up wasd-controls for AR mode');
    }
    
    console.log('Camera configured for AR mode.');
    
  // Dependencies: only scene readiness and camera ref
  }, [isSceneReady, cameraRef]); 
} 